import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface SelectedOfferTierPrice {
  id: number;
  quantity: number;
  price: number | null;
  acceptedDate: string | null;
  isInOfferedOffer: string | null;
  rowLabel: string;
  currencyCode: string;
  exchangeRate: number;
  inquiryPrices: {
    inquiryId: number;
    inquiryNumber: string;
    price: number | null;
  }[];
}

interface ContextValue {
  selectedTierPriceIds: Set<number>;
  selectedTierPriceIdsList: number[];
  selectedTierPrices: SelectedOfferTierPrice[];
  unapprovedSelectedTierPrices: SelectedOfferTierPrice[];
  approvedSelectedTierPrices: SelectedOfferTierPrice[];
  selectedCount: number;
  makeOfferTierPriceIds?: number[];
  isMakeOfferOpen: boolean;
  registerTierPrices: (tierPrices: SelectedOfferTierPrice[]) => void;
  openMakeOffer: () => void;
  openMakeOfferWithAllPrices: () => void;
  closeMakeOffer: () => void;
  clearSelection: () => void;
  toggleTierPrice: (id: number) => void;
  selectTierPrices: (ids: number[]) => void;
  clearTierPrices: (ids: number[]) => void;
  toggleTierPrices: (ids: number[]) => void;
}

const OfferedOfferSelectionContext = createContext<ContextValue | null>(null);

export function OfferedOfferSelectionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [selectedTierPriceIds, setSelectedTierPriceIds] = useState<Set<number>>(
    new Set(),
  );
  const [tierPriceDetails, setTierPriceDetails] = useState<
    Map<number, SelectedOfferTierPrice>
  >(new Map());
  const {
    value: makeOfferTierPriceIds,
    isOpen: isMakeOfferOpen,
    onOpen,
    onClose: closeMakeOffer,
  } = useValueDisclosure<number[]>();

  const selectedTierPriceIdsList = useMemo(
    () => Array.from(selectedTierPriceIds),
    [selectedTierPriceIds],
  );
  const selectedTierPrices = useMemo(
    () =>
      selectedTierPriceIdsList
        .map((id) => tierPriceDetails.get(id))
        .filter((price): price is SelectedOfferTierPrice => !!price),
    [selectedTierPriceIdsList, tierPriceDetails],
  );
  const unapprovedSelectedTierPrices = useMemo(
    () => selectedTierPrices.filter((price) => !price.acceptedDate),
    [selectedTierPrices],
  );
  const approvedSelectedTierPrices = useMemo(
    () => selectedTierPrices.filter((price) => !!price.acceptedDate),
    [selectedTierPrices],
  );

  const registerTierPrices = useCallback(
    (tierPrices: SelectedOfferTierPrice[]) => {
      setTierPriceDetails((prev) => {
        const next = new Map(prev);
        let changed = false;
        for (const tierPrice of tierPrices) {
          const current = next.get(tierPrice.id);
          const inquiryPricesChanged =
            current == null ||
            JSON.stringify(current.inquiryPrices) !==
              JSON.stringify(tierPrice.inquiryPrices);
          if (
            current == null ||
            current.quantity !== tierPrice.quantity ||
            current.price !== tierPrice.price ||
            current.acceptedDate !== tierPrice.acceptedDate ||
            current.isInOfferedOffer !== tierPrice.isInOfferedOffer ||
            current.rowLabel !== tierPrice.rowLabel ||
            current.currencyCode !== tierPrice.currencyCode ||
            current.exchangeRate !== tierPrice.exchangeRate ||
            inquiryPricesChanged
          ) {
            changed = true;
          }
          next.set(tierPrice.id, tierPrice);
        }
        return changed ? next : prev;
      });
    },
    [],
  );

  const clearSelection = useCallback(() => {
    setSelectedTierPriceIds(new Set());
  }, []);

  const toggleTierPrice = useCallback((id: number) => {
    setSelectedTierPriceIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const selectTierPrices = useCallback((ids: number[]) => {
    setSelectedTierPriceIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const clearTierPrices = useCallback((ids: number[]) => {
    setSelectedTierPriceIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const toggleTierPrices = useCallback(
    (ids: number[]) => {
      const allSelected = ids.every((id) => selectedTierPriceIds.has(id));
      if (allSelected) {
        clearTierPrices(ids);
        return;
      }
      selectTierPrices(ids);
    },
    [clearTierPrices, selectTierPrices, selectedTierPriceIds],
  );

  const openMakeOffer = useCallback(() => {
    onOpen(
      selectedTierPrices
        .filter((tierPrice) => tierPrice.price != null && tierPrice.price > 0)
        .map((tierPrice) => tierPrice.id),
    );
  }, [onOpen, selectedTierPrices]);

  const openMakeOfferWithAllPrices = useCallback(() => {
    onOpen(
      Array.from(tierPriceDetails.values())
        .filter((tierPrice) => tierPrice.price != null && tierPrice.price > 0)
        .map((tierPrice) => tierPrice.id),
    );
  }, [onOpen, tierPriceDetails]);

  const value = useMemo<ContextValue>(
    () => ({
      selectedTierPriceIds,
      selectedTierPriceIdsList,
      selectedTierPrices,
      unapprovedSelectedTierPrices,
      approvedSelectedTierPrices,
      selectedCount: selectedTierPriceIds.size,
      makeOfferTierPriceIds: makeOfferTierPriceIds ?? undefined,
      isMakeOfferOpen,
      registerTierPrices,
      openMakeOffer,
      openMakeOfferWithAllPrices,
      closeMakeOffer,
      clearSelection,
      toggleTierPrice,
      selectTierPrices,
      clearTierPrices,
      toggleTierPrices,
    }),
    [
      selectedTierPriceIds,
      selectedTierPriceIdsList,
      selectedTierPrices,
      unapprovedSelectedTierPrices,
      approvedSelectedTierPrices,
      makeOfferTierPriceIds,
      isMakeOfferOpen,
      registerTierPrices,
      openMakeOffer,
      openMakeOfferWithAllPrices,
      closeMakeOffer,
      clearSelection,
      toggleTierPrice,
      selectTierPrices,
      clearTierPrices,
      toggleTierPrices,
    ],
  );

  return (
    <OfferedOfferSelectionContext.Provider value={value}>
      {children}
    </OfferedOfferSelectionContext.Provider>
  );
}

export function useOfferedOfferSelection() {
  const context = useContext(OfferedOfferSelectionContext);
  if (!context) {
    throw new Error(
      'useOfferedOfferSelection must be used within OfferedOfferSelectionProvider',
    );
  }
  return context;
}
